import type { ActivityEvent } from './feed-timeline'

// --- Shared types for feed items (mirrors backend schema) ---

interface BasePrescreenItem {
  type: 'prescreen'
  id: string
  date: string
  score: number | null
  verdict: string | null
  verdictStatus: 'strong' | 'moderate' | 'weak' | null
  categories: { label: string; score: number | null; status: 'strong' | 'moderate' | 'weak' }[]
  evaluationId: string | null
  prescreenStatus: string
}

interface PitchSessionItem {
  type: 'pitch_session'
  id: string
  date: string
  sessionNumber: number
  score: number | null
  prevScore: number | null
  readiness: string | null
  readinessColor: 'emerald' | 'amber' | 'red' | null
  metrics: { label: string; score: number; delta: number | null }[]
  insights: { text: string; comparison: 'improved' | 'regressed' | 'neutral' }[]
  verdictTitle: string | null
  evaluationId: string | null
  pitchSessionId: string
  sessionStatus: string
  evaluationState: string | null
}

export interface InvestorPrescreenItem extends BasePrescreenItem {
  startupName: string
  prescreenStatus: string
  evaluationType: 'prescreen' | 'full'
}

export type FounderFeedItem = BasePrescreenItem | PitchSessionItem

// --- Helpers ---

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function mapPrescreenItem(
  item: BasePrescreenItem,
  basePath: string,
  title: string,
  evaluationType?: 'prescreen' | 'full'
): Extract<ActivityEvent, { type: 'prescreen' }> {
  return {
    id: item.id,
    type: 'prescreen',
    date: formatDate(item.date),
    title,
    score: item.score ?? null,
    verdict: item.verdict ?? 'Pending',
    verdictStatus: item.verdictStatus ?? 'weak',
    summary: '',
    categories: item.categories,
    link: item.evaluationId ? `${basePath}/evaluation/${item.evaluationId}` : `${basePath}/`,
    evaluationType,
  }
}

// --- Status derivation ---

type DerivedStatus = {
  readiness: string
  color?: 'emerald' | 'amber' | 'red'
  statusDetail?: string
}

function deriveStatus(item: PitchSessionItem): DerivedStatus {
  // If we have a readiness from the backend (completed eval with score), use it
  if (item.readiness) return { readiness: item.readiness }

  if (item.evaluationState === 'PROCESSING' || item.evaluationState === 'PENDING') {
    return {
      readiness: 'Evaluating...',
      color: 'amber',
      statusDetail: 'Our investor council is reviewing your pitch',
    }
  }
  if (item.evaluationState === 'FAILED') {
    return {
      readiness: 'Evaluation Failed',
      color: 'red',
      statusDetail: 'Something went wrong during evaluation. Try starting a new session.',
    }
  }
  if (item.sessionStatus === 'INCOMPLETE') {
    return {
      readiness: 'Incomplete',
      color: 'amber',
      statusDetail: 'Session was interrupted before it could finish',
    }
  }
  if (item.sessionStatus === 'FAILED') {
    return {
      readiness: 'Failed',
      color: 'red',
      statusDetail: 'Something went wrong. Try starting a new session.',
    }
  }
  if (item.sessionStatus === 'IN_PROGRESS') {
    return { readiness: 'In Progress', color: 'amber' }
  }
  if (item.sessionStatus === 'COMPLETED') {
    return {
      readiness: 'Awaiting Evaluation',
      color: 'amber',
      statusDetail: 'Session completed, evaluation will start shortly',
    }
  }
  return { readiness: item.sessionStatus }
}

function isPitchSessionActive(sessionStatus: string): boolean {
  return (
    sessionStatus === 'PENDING' || sessionStatus === 'ACTIVE' || sessionStatus === 'IN_PROGRESS'
  )
}

// --- Public mappers ---

export function mapFounderFeedItems(items: FounderFeedItem[], basePath: string): ActivityEvent[] {
  return items.map((item) => {
    if (item.type === 'prescreen') {
      const processing = item.prescreenStatus === 'PENDING' || item.prescreenStatus === 'PROCESSING'
      const title = processing ? 'Pre-screen: evaluating' : 'Pre-screen completed'
      const mapped = mapPrescreenItem(item, basePath, title)
      return { ...mapped, processing }
    }

    const status = deriveStatus(item)

    return {
      id: item.id,
      type: 'pitch' as const,
      date: formatDate(item.date),
      title: `Pitch Session #${item.sessionNumber}`,
      score: item.score,
      prevScore: item.prevScore ?? undefined,
      readiness: status.readiness,
      readinessColor: item.readinessColor ?? status.color ?? null,
      metrics: item.metrics.map((m) => ({
        label: m.label,
        score: m.score,
        prevScore: m.delta !== null ? m.score - m.delta : undefined,
      })),
      insights: item.insights.map((i) => ({
        text: i.text,
        comparison: i.comparison === 'neutral' ? undefined : i.comparison,
      })),
      hasDetails: !!item.evaluationId,
      link: item.evaluationId
        ? `${basePath}/evaluation/${item.evaluationId}`
        : isPitchSessionActive(item.sessionStatus)
          ? `${basePath}/pitch`
          : undefined,
      verdictTitle: item.verdictTitle,
      sessionStatus: item.sessionStatus,
      evaluationState: item.evaluationState,
      statusDetail: status.statusDetail,
    }
  })
}

export function mapInvestorFeedItems(
  items: InvestorPrescreenItem[],
  basePath: string
): ActivityEvent[] {
  return items.map((item) => {
    const processing = item.prescreenStatus === 'PENDING' || item.prescreenStatus === 'PROCESSING'
    const mapped = mapPrescreenItem(
      item,
      basePath,
      `${item.startupName}: Custom Evaluation`,
      item.evaluationType
    )
    return { ...mapped, processing }
  })
}
